/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import * as XLSX from "xlsx";
import adminReportsService from "../../services/admin-reports.service";
import SearchService from "../../services/search.service";
import { Pagination } from "../Pagination";
import "./index.scss";

const REPORT_BASE_OPTIONS = [
  { value: "branch", label: "Branch" },
  { value: "patient", label: "Patient" },
];

const SORT_ORDER_OPTIONS = [
  { value: "desc", label: "Descending" },
  { value: "asc", label: "Ascending" },
];

const DISPLAY_COLUMNS = [
  { key: "id", label: "#" },
  { key: "rxNumber", label: "Rx #" },
  { key: "patientName", label: "Patient" },
  { key: "fromPharmacyBranchName", label: "From Branch" },
  { key: "fromPharmacyBranchCity", label: "From City" },
  { key: "fromPharmacyBranchState", label: "From State" },
  { key: "toPharmacyBranchName", label: "To Branch" },
  { key: "toPharmacyBranchCity", label: "To City" },
  { key: "toPharmacyBranchState", label: "To State" },
  { key: "statusName", label: "Status" },
  { key: "createdOn", label: "Created" },
];

const SORT_BY_OPTIONS = DISPLAY_COLUMNS.map((col) => ({
  value: col.key,
  label: col.label,
}));

const SEARCHABLE_SELECT_PROPS = {
  classNamePrefix: "react-select",
  isSearchable: true,
  menuPortalTarget: document.body,
  styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

const AdminTransferReportsDataGrid = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [exportFields, setExportFields] = useState(new Set());

  const [stateId, setStateId] = useState("");
  const [cityId, setCityId] = useState("");
  const [reportBase, setReportBase] = useState("branch");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("createdOn");
  const [sortOrder, setSortOrder] = useState("desc");
  const [excludedPharmacies, setExcludedPharmacies] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);

  const excludeBranchIds = excludedPharmacies.map((p) => p.id);

  const isBranchBase = reportBase === "branch";

  const filters = {
    reportBase,
    stateId: stateId || undefined,
    cityId: isBranchBase && cityId ? cityId : undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    keyword: searchText || undefined,
    sortBy,
    sortOrder,
    excludeBranchIds: excludeBranchIds.length ? excludeBranchIds : undefined,
  };

  const loadPharmacyOptions = async (inputValue) => {
    try {
      const data = await SearchService.Search(
        "pharmacies",
        inputValue,
        50,
        1,
        "id",
        "asc",
        false,
        "admin"
      );
      const excludedIds = new Set(excludedPharmacies.map((p) => String(p.id)));
      return (data?.data || []).filter((o) => !excludedIds.has(String(o.id)));
    } catch {
      return [];
    }
  };

  const loadList = useCallback(
    async (pageNum) => {
      setLoading(true);
      const result = await adminReportsService.ListTransferRequests({
        page: pageNum,
        limit,
        ...filters,
      });
      if (result.success) {
        setRecords(result.data?.data || []);
        setTotal(result.data?.total || 0);
      }
      setLoading(false);
    },
    [limit, reportBase, stateId, cityId, dateFrom, dateTo, searchText, sortBy, sortOrder, excludeBranchIds.join(",")]
  );

  useEffect(() => {
    const init = async () => {
      const [statesRes, fieldsRes] = await Promise.all([
        adminReportsService.ListStates(),
        adminReportsService.ListFields(),
      ]);
      if (statesRes.success) setStates(statesRes.data || []);
      if (fieldsRes.success) {
        const defs = fieldsRes.data || [];
        setFieldDefs(defs);
        setExportFields(new Set(defs.map((f) => f.key)));
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (!isBranchBase || !stateId) {
      setCities([]);
      if (!isBranchBase) setCityId("");
      return;
    }
    const loadCities = async () => {
      const result = await adminReportsService.ListCities(stateId);
      if (result.success) {
        setCities(result.data || []);
      }
    };
    loadCities();
  }, [stateId, isBranchBase]);

  useEffect(() => {
    loadList(page);
  }, [page, loadList]);

  const debouncedSearch = debounce((keyword) => {
    setSearchText(keyword);
    setPage(1);
  }, 300);

  const handleApplyFilters = () => {
    setPage(1);
    loadList(1);
  };

  const handleClearFilters = () => {
    setReportBase("branch");
    setStateId("");
    setCityId("");
    setDateFrom("");
    setDateTo("");
    setSearchText("");
    setSortBy("createdOn");
    setSortOrder("desc");
    setExcludedPharmacies([]);
    setPage(1);
    setTimeout(() => {
      adminReportsService
        .ListTransferRequests({
          page: 1,
          limit,
          reportBase: "branch",
          sortBy: "createdOn",
          sortOrder: "desc",
          excludeBranchIds: [],
        })
        .then((result) => {
          if (result.success) {
            setRecords(result.data?.data || []);
            setTotal(result.data?.total || 0);
          }
        });
    }, 0);
  };

  const handleSortColumn = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const toggleExportField = (key) => {
    setExportFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const exportToExcel = async () => {
    const fields = Array.from(exportFields);
    if (fields.length === 0) {
      Swal.fire({ icon: "warning", title: "Select at least one column to export." });
      return;
    }

    setExporting(true);
    try {
      const result = await adminReportsService.ExportTransferRequests({
        ...filters,
        fields,
      });

      if (!result.success) {
        Swal.fire({
          icon: "error",
          title: result.error?.message || "Export failed",
        });
        return;
      }

      const { data, columns, truncated, total: exportTotal } = result.data || {};
      if (!data?.length) {
        Swal.fire({ icon: "info", title: "No records to export for the selected filters." });
        return;
      }

      const headerRow = columns.map((c) => c.label);
      const dataRows = data.map((row) =>
        columns.map((col) => (row[col.key] != null ? row[col.key] : ""))
      );
      const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Transfer Requests");
      XLSX.writeFile(
        wb,
        `transfer-requests-report-${moment().format("YYYY-MM-DD-HHmm")}.xlsx`
      );

      if (truncated) {
        Swal.fire({
          icon: "warning",
          title: `Exported ${exportTotal} rows (maximum limit reached).`,
        });
      }
      setShowExportModal(false);
    } finally {
      setExporting(false);
    }
  };

  const displayColumns = DISPLAY_COLUMNS;

  const stateOptions = useMemo(
    () =>
      states.map((s) => ({
        value: String(s.id),
        label: `${s.name} (${s.code})`,
      })),
    [states]
  );

  const cityOptions = useMemo(
    () =>
      cities.map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    [cities]
  );

  const selectedReportBase =
    REPORT_BASE_OPTIONS.find((o) => o.value === reportBase) || REPORT_BASE_OPTIONS[0];
  const selectedState = stateOptions.find((o) => o.value === String(stateId)) || null;
  const selectedCity = cityOptions.find((o) => o.value === String(cityId)) || null;
  const selectedSortBy = SORT_BY_OPTIONS.find((o) => o.value === sortBy) || SORT_BY_OPTIONS[0];
  const selectedSortOrder =
    SORT_ORDER_OPTIONS.find((o) => o.value === sortOrder) || SORT_ORDER_OPTIONS[0];

  return (
    <div className="admin-transfer-reports-datagrid">
      <div className="card h-100">
        <div className="card-header bg-white">
          <div className="row g-2 align-items-end">
            <div className="col-md-3 reports-filter-select">
              <label className="form-label small mb-1">Base for report</label>
              <Select
                {...SEARCHABLE_SELECT_PROPS}
                options={REPORT_BASE_OPTIONS}
                value={selectedReportBase}
                onChange={(option) => {
                  const nextBase = option?.value || "branch";
                  setReportBase(nextBase);
                  if (nextBase === "patient") setCityId("");
                  setPage(1);
                }}
                isClearable={false}
                placeholder="Select base…"
              />
            </div>
            <div className="col-md-3 reports-filter-select">
              <label className="form-label small mb-1">
                State{!isBranchBase ? " (patient StateText)" : ""}
              </label>
              <Select
                {...SEARCHABLE_SELECT_PROPS}
                options={stateOptions}
                value={selectedState}
                onChange={(option) => {
                  setStateId(option?.value || "");
                  setCityId("");
                  setPage(1);
                }}
                isClearable
                placeholder="All states"
              />
            </div>
            {isBranchBase && (
              <div className="col-md-2 reports-filter-select">
                <label className="form-label small mb-1">City</label>
                <Select
                  {...SEARCHABLE_SELECT_PROPS}
                  options={cityOptions}
                  value={selectedCity}
                  onChange={(option) => {
                    setCityId(option?.value || "");
                    setPage(1);
                  }}
                  isClearable
                  isDisabled={!stateId}
                  placeholder={stateId ? "All cities in state" : "Select state first"}
                />
              </div>
            )}
            <div className="col-md-2">
              <label className="form-label small mb-1">Date from</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Date to</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="col-md-2 d-flex gap-1">
              <button type="button" className="btn btn-primary flex-grow-1" onClick={handleApplyFilters}>
                Search
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={handleClearFilters} title="Clear filters">
                <i className="fa fa-times"></i>
              </button>
            </div>
          </div>
          <div className="row g-2 align-items-end mt-2">
            <div className="col-12 exclude-pharmacies-select">
              <label className="form-label small mb-1">Exclude pharmacies</label>
              <AsyncSelect
                isMulti
                isSearchable
                cacheOptions
                defaultOptions
                loadOptions={loadPharmacyOptions}
                value={excludedPharmacies}
                onChange={(selected) => {
                  setExcludedPharmacies(selected || []);
                  setPage(1);
                }}
                getOptionValue={(o) => String(o.id)}
                getOptionLabel={(o) => o.label}
                placeholder="Search pharmacies to exclude from results…"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
              <small className="text-muted">
                Transfers where the from or to branch is an excluded pharmacy will not appear.
              </small>
            </div>
          </div>
          <div className="row g-2 align-items-end mt-2">
            <div className="col-md-3 reports-filter-select">
              <label className="form-label small mb-1">Sort by</label>
              <Select
                {...SEARCHABLE_SELECT_PROPS}
                options={SORT_BY_OPTIONS}
                value={selectedSortBy}
                onChange={(option) => {
                  setSortBy(option?.value || "createdOn");
                  setPage(1);
                }}
                isClearable={false}
                placeholder="Sort by…"
              />
            </div>
            <div className="col-md-2 reports-filter-select">
              <label className="form-label small mb-1">Order</label>
              <Select
                {...SEARCHABLE_SELECT_PROPS}
                options={SORT_ORDER_OPTIONS}
                value={selectedSortOrder}
                onChange={(option) => {
                  setSortOrder(option?.value || "desc");
                  setPage(1);
                }}
                isClearable={false}
                placeholder="Order…"
              />
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-success"
              onClick={() => setShowExportModal(true)}
            >
              <i className="fa-solid fa-file-excel me-1"></i>
              Export to Excel
            </button>
            <div className="search-box">
              <i className="fa fa-search"></i>
              <form onSubmit={(event) => event.preventDefault()}>
                <input
                  type="text"
                  className="form-control rounded-pill"
                  placeholder="Search patient, branch, Rx #…"
                  spellCheck="false"
                  onChange={(event) => debouncedSearch(event.target.value)}
                />
              </form>
            </div>
          </div>
        </div>

        <div className="card-body datagrid p-0 position-relative">
          {loading && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 2 }}>
              <span className="spinner-border text-primary"></span>
            </div>
          )}
          <table className="table mb-0 table-hover">
            <thead>
              <tr>
                {displayColumns.map((col) => (
                  <th
                    key={col.key}
                    className="sortable-th"
                    onClick={() => handleSortColumn(col.key)}
                    title="Click to sort"
                  >
                    {col.label}
                    {sortBy === col.key && (
                      <i
                        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} ms-1`}
                      ></i>
                    )}
                    {sortBy !== col.key && (
                      <i className="fa-solid fa-sort ms-1 text-muted"></i>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length} className="text-center text-muted py-4">
                    No transfer requests found.
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr key={row.id}>
                    {displayColumns.map((col) => (
                      <td key={col.key}>
                        {col.key === "createdOn" && row[col.key]
                          ? moment(row[col.key]).format("MM/DD/YYYY HH:mm")
                          : row[col.key] ?? ""}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination page={page} total={total} limit={limit} setPage={setPage} maxPage={20} />
      </div>

      {showExportModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Export to Excel</h5>
                <button type="button" className="btn-close" onClick={() => setShowExportModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  All matching records will be exported (not only the current page). Current filters apply.
                </p>
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => setExportFields(new Set(fieldDefs.map((f) => f.key)))}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setExportFields(new Set())}
                  >
                    Deselect all
                  </button>
                </div>
                <div className="row">
                  {fieldDefs.map((field) => (
                    <div className="col-md-4 col-sm-6 mb-2" key={field.key}>
                      <label className="form-check-label d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input m-0"
                          checked={exportFields.has(field.key)}
                          onChange={() => toggleExportField(field.key)}
                        />
                        {field.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowExportModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={exportToExcel}
                  disabled={exporting}
                >
                  {exporting ? "Exporting…" : "Download Excel"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransferReportsDataGrid;
